import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { ChartSkeleton, TableSkeleton } from '../components/LoadingSkeleton';
import { DataTable } from '../components/DataTable';
import { HuaweiOrder } from '../lib/supabase';
const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const LIMIT = 10;
export function FrequencyAnalysis() {
  const {
    data: allData,
    loading: allLoading,
    error
  } = useSupabaseQuery({
    limit: 5000
  });
  const [page, setPage] = useState(0);
  const {
    data: tableData,
    count,
    loading: tableLoading
  } = useSupabaseQuery({
    limit: LIMIT,
    page
  });
  const chartData = useMemo(() => {
    if (!allData.length) return {
      freqGroupDist: [],
      freqByTreg: []
    };
    const groupMap = new Map();
    const tregFreqMap = new Map();
    allData.forEach(item => {
      if (item.FreqGrouping) groupMap.set(item.FreqGrouping, (groupMap.get(item.FreqGrouping) || 0) + 1);
      if (item.TREG && item.Frekuensi) {
        tregFreqMap.set(item.TREG, (tregFreqMap.get(item.TREG) || 0) + 1);
      }
    });
    return {
      freqGroupDist: Array.from(groupMap, ([name, value]) => ({
        name,
        value
      })).sort((a, b) => b.value - a.value),
      freqByTreg: Array.from(tregFreqMap, ([name, value]) => ({
        name,
        value
      })).sort((a, b) => b.value - a.value)
    };
  }, [allData]);
  if (error) return <div className="p-6 text-red-400 bg-red-950/30 rounded-xl border border-red-900" data-id="element-671" data-mp="1cwf023yjf77c">
        Error: {error}
      </div>;
  const columns = [{
    header: 'Site ID',
    accessor: 'Site_ID_NE',
    render: val => <span className="text-cyan-400 font-medium" data-id="element-672" data-mp="2o9r8ks58sdzu">{val}</span>
  }, {
    header: 'Frequency',
    accessor: 'Frekuensi'
  }, {
    header: 'Freq Grouping',
    accessor: 'FreqGrouping'
  }, {
    header: 'Site Type',
    accessor: 'Tipe_Site'
  }];
  return <div className="space-y-6" data-id="element-673" data-mp="28jrte7t9hvot">
      <h2 className="text-xl font-bold text-slate-100" data-id="element-674" data-mp="28iqf7ugy8t2v">Frequency Analysis</h2>

      {/* ================= CHART ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-675" data-mp="1fvvx347lu5sk">
        {allLoading ? <>
            <ChartSkeleton data-id="element-676" data-mp="1ay3u9f5f8z9l" />
            <ChartSkeleton data-id="element-677" data-mp="1ayvpgov3wuzc" />
          </> : <>
            {/* PIE */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="glass-panel p-6 rounded-xl h-[420px] flex flex-col" data-id="element-678" data-mp="1pn0aw0ljnklt">
              <h3 className="text-sm font-semibold text-slate-100 mb-4 uppercase tracking-wider" data-id="element-679" data-mp="x4cw12x551t6">
                Frequency Grouping Distribution
              </h3>

              <div className="flex-1 min-h-0" data-id="element-680" data-mp="33twn6d3w9nb3">
                <ResponsiveContainer data-id="element-681" data-mp="33s3fqvlgiz21">
                  <PieChart data-id="element-682" data-mp="38leox3dqrrx4">
                    <Pie data={chartData.freqGroupDist} cx="50%" cy="45%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({
                  name,
                  percent
                }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} stroke="#020617" strokeWidth={2} data-id="element-683" data-mp="38lohmyq68d6i">
                      {chartData.freqGroupDist.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} data-id="element-684" data-mp="3hnfinq9rnvu1" data-mpi={index} />)}
                    </Pie>

                    <Tooltip contentStyle={{
                  backgroundColor: '#020617',
                  border: '1px solid #334155',
                  borderRadius: '10px',
                  color: '#e2e8f0'
                }} itemStyle={{
                  color: '#ffffff'
                }} cursor={{
                  fill: 'rgba(255,255,255,0.05)'
                }} data-id="element-685" data-mp="2mdkd8c18vjho" />

                    <Legend verticalAlign="bottom" align="center" wrapperStyle={{
                  fontSize: '11px',
                  color: '#cbd5f5',
                  paddingTop: '10px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '8px'
                }} formatter={value => <span style={{
                  color: '#e2e8f0'
                }} data-id="element-687" data-mp="1fwp6hkunwpkq">{value}</span>} data-id="element-686" data-mp="35col3hh1v77r" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* BAR */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="glass-panel p-6 rounded-xl h-[420px] flex flex-col" data-id="element-688" data-mp="4g49ojo6z89y">
              <h3 className="text-sm font-semibold text-slate-100 mb-4 uppercase tracking-wider" data-id="element-689" data-mp="e55ucn5omdu9">
                Frequency Count per TREG
              </h3>

              <div className="flex-1 min-h-0" data-id="element-690" data-mp="n6vkzqrqp8qz">
                <ResponsiveContainer data-id="element-691" data-mp="n75918t855u1">
                  <BarChart data={chartData.freqByTreg} data-id="element-692" data-mp="s5q834wqpjgw">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} data-id="element-693" data-mp="s56u1za7nf85" />

                    <XAxis dataKey="name" stroke="#cbd5f5" fontSize={12} tickLine={false} axisLine={false} data-id="element-694" data-mp="2ty4ou0njshvq" />

                    <YAxis stroke="#cbd5f5" fontSize={12} tickLine={false} axisLine={false} data-id="element-695" data-mp="3saz35gz6vekg" />

                    <Tooltip contentStyle={{
                  backgroundColor: '#020617',
                  border: '1px solid #334155',
                  borderRadius: '10px',
                  color: '#e2e8f0'
                }} itemStyle={{
                  color: '#ffffff'
                }} cursor={{
                  fill: 'rgba(255,255,255,0.05)'
                }} data-id="element-696" data-mp="56jcdeu381j1" />

                    <Bar dataKey="value" fill="#f59e0b" radius={[6, 6, 0, 0]} data-id="element-697" data-mp="12rfb8jddyc1n" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </>}
      </div>

      {/* ================= TABLE ================= */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-698" data-mp="1mdqmsrbqbzf9">
        <h3 className="text-sm font-semibold text-slate-100 mb-4 uppercase tracking-wider" data-id="element-699" data-mp="17fg4sksxg0kp">
          Frequency Details
        </h3>

        {tableLoading ? <TableSkeleton data-id="element-700" data-mp="yo63g7bnz05g" /> : <DataTable data={tableData} columns={columns} page={page} totalPages={Math.ceil(count / LIMIT)} onPageChange={setPage} data-id="element-701" data-mp="yn4nppjpdphe" />}
      </motion.div>
    </div>;
}